// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useState } from 'react';

import type { BendTypeName } from '@evolvemep/api-ts';
import { Box } from '@mantine/core';

import { isNotNil } from 'helpers/isNotNil';
import type { BendPreviewSpecifications } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewGeometry';
import type { FitInsets } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewRenderer';
import { BendRenderCanvas } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendRenderCanvas';
import classes from 'modules/Shop/EvolveFabrication/BendCreator/Shell/BendCreatorShell.module.css';
import { type ShellViewMode, ShellTopBar } from 'modules/Shop/EvolveFabrication/BendCreator/Shell/ShellTopBar';
import { STARTING_BEND_TYPE } from 'modules/Shop/EvolveFabrication/BendCreator/Shell/useBendCreatorPreviewData';

/** In CSS pixels. Keep in step with the sizes in BendCreatorShell.module.css. */
const GUTTER = 20;
const NAV_PANEL_WIDTH = 375;
const CART_WIDTH = 300;
const TOP_BAR_HEIGHT = 40;

/**
 * The space the floating UI covers. The cart's column is always reserved, even while the cart is hidden,
 * so the model never moves or rescales when the cart opens or collapses.
 */
const FIT_INSETS: FitInsets = {
  top: GUTTER + TOP_BAR_HEIGHT + GUTTER,
  right: GUTTER + CART_WIDTH + GUTTER,
  bottom: GUTTER,
  left: GUTTER + NAV_PANEL_WIDTH + GUTTER,
};

type Props = {
  /** When null, the canvas shows a placeholder Stub 90. */
  bendType: BendTypeName | null;
  /** When null, the bend type preview is drawn from default dimensions. */
  bendPreviewData: BendPreviewSpecifications | null;
  /** Content of the floating panel at the left. */
  navPanel: ReactNode;
  /** Content floating at the top right, such as the bend list. It draws its own panel. */
  cartPanel?: ReactNode;
  /** When omitted, the shell has no Exit button and the caller owns closing. */
  onClose?: () => void;
  /** Bends in the bend list that have not been added to a work order. Exiting asks for confirmation when above 0. */
  bendsToDiscard: number;
};

/**
 * The fullscreen Bend Creator layout: the bend render fills the shell, with the nav panel floating at the left,
 * the cart floating at the right, and the view controls floating at the top. Must be rendered inside a `BendPreviewContextProvider`.
 */
export const BendCreatorShell = ({
  bendType,
  bendPreviewData,
  navPanel,
  cartPanel,
  onClose,
  bendsToDiscard,
}: Props) => {
  const [viewMode, setViewMode] = useState<ShellViewMode>('3d');
  // The model orbits slowly, to show that it can be turned, until the user moves it or uses the top bar.
  const [orbiting, setOrbiting] = useState(true);

  const layoutAvailable = isNotNil(bendPreviewData?.layoutDimensions);
  // "layout" is not a render mode: it draws the flat tube in the front view.
  // Fall back to the front view if layout was selected but is no longer available.
  const showLayout = viewMode === 'layout' && layoutAvailable;
  const renderMode = viewMode === 'layout' ? '2dFront' : viewMode;

  return (
    <Box className={classes.shell}>
      <Box className={classes.canvasLayer}>
        <BendRenderCanvas
          bendType={bendType ?? STARTING_BEND_TYPE}
          bendPreviewData={bendPreviewData}
          renderMode={renderMode}
          showLayout={showLayout}
          fitInsets={FIT_INSETS}
          autoOrbit={orbiting}
        />
      </Box>
      <Box className={classes.navPanel}>{navPanel}</Box>
      {isNotNil(cartPanel) && <Box className={classes.cartSlot}>{cartPanel}</Box>}
      <ShellTopBar
        viewMode={viewMode === 'layout' && !layoutAvailable ? renderMode : viewMode}
        onViewModeChange={setViewMode}
        onInteract={() => setOrbiting(false)}
        layoutAvailable={layoutAvailable}
        onClose={onClose}
        bendsToDiscard={bendsToDiscard}
      />
    </Box>
  );
};
